import { Radio, Spin } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import LeftArrow from '@/assets/icons/monitor-back.svg?react'
import { Divider, Trans } from '@/components'
import { useDomain } from '@/contexts/DomainContext'
import { useInstanceMonitorData } from '@/hooks/useInstanceMonitorData'
import { ChartType } from '@/models/chart'
import { Chart, MonitorItemData } from '@/pages/instance-list/components/MonitorChart'
import { formatDomain } from '@/utils/formatter'
import { TIME_UNITS_IN_SECONDS } from '@/utils/time'

export function MonitorDetailPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { domainInfos } = useDomain()
  const instance = location.state?.instance || {}
  const from = location.state?.from
  const [filterDuration, setFilterDuration] = useState(TIME_UNITS_IN_SECONDS.HOUR)

  const handleBack = () => {
    if (from) {
      navigate(from)
    } else {
      navigate(-1)
    }
  }

  const { monitorDatas, initialLoading, refreshing } = useInstanceMonitorData({
    instance,
    filterDuration,
  })

  return (
    <div className="card bg-panel-white">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-8">
        <div className="flex min-w-0 items-center gap-4">
          <button
            type="button"
            onClick={handleBack}
            className="w-11 h-11 icon-btn shrink-0"
            aria-label={t('common.back')}
          >
            <LeftArrow />
          </button>
          <div className="min-w-0">
            <div className="h4 truncate">{instance?.displayTitle}</div>
            <div className="t-sm mt-1 flex gap-2">
              <span className="text-secondary">
                {domainInfos.find((info) => info.domain === instance?.domain)?.displayName ||
                  formatDomain(instance?.domain ?? 0)}
              </span>
              <span className="text-tertiary">·</span>
              <span className={instance?.statusTextStyle}>{instance?.statusText}</span>
            </div>
          </div>
        </div>
        <Radio.Group
          onChange={(e) => {
            setFilterDuration(e.target.value)
          }}
          value={filterDuration}
        >
          <Radio.Button value={TIME_UNITS_IN_SECONDS.HOUR}>
            {t('instance.detail.monitor.filterRealtime')}
          </Radio.Button>
          <Radio.Button value={TIME_UNITS_IN_SECONDS.DAY}>
            {t('instance.detail.monitor.filter1Day')}
          </Radio.Button>
          <Radio.Button value={3 * TIME_UNITS_IN_SECONDS.DAY}>
            {t('instance.detail.monitor.filter3Days')}
          </Radio.Button>
        </Radio.Group>
      </div>
      <Divider className="mb-8" />

      {(initialLoading || refreshing) && monitorDatas.length === 0 ? (
        <div className="h-16 overflow-hidden mt-4 text-center text-tertiary t-sm">
          {t('instance.detail.monitor.loading')}
        </div>
      ) : Array.isArray(monitorDatas) && monitorDatas.length > 0 ? (
        <Spin spinning={refreshing} tip={t('instance.detail.monitor.loading')}>
          <div
            className="text-tertiary gap-8 grid grid-cols-2 md:grid-cols-1"
            aria-busy={refreshing}
          >
            {monitorDatas.map((item: MonitorItemData) => {
              return (
                <div
                  key={item.id}
                  className={
                    item.gpuIndex === 0 && item.type === ChartType.GPUCore
                      ? 'min-w-0 col-start-1'
                      : 'min-w-0'
                  }
                >
                  <div className="mb-2 flex justify-between items-start gap-4 min-h-10">
                    <div className="min-w-0 flex flex-col gap-1">
                      {item.title}
                      {item.description}
                    </div>
                    {item.realtime}
                  </div>
                  <Chart value={item}></Chart>
                </div>
              )
            })}
          </div>
        </Spin>
      ) : (
        <div className="h-16 overflow-hidden mt-4 text-center text-tertiary t-sm">
          <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
        </div>
      )}
    </div>
  )
}
